import React, { useState, useMemo, useRef } from 'react';
import { InventoryData, InventoryItem, Department, StaffMember } from '../lib/types';
import { inventoryAdd, inventoryIssue, inventoryReturn, inventoryDelete, inventoryRename, inventoryMerge } from '../lib/api';
import { ConfirmModal } from './ui';
import { InventoryStatCards } from './inventory/InventoryStatCards';
import { InventoryFilterBar } from './inventory/InventoryFilterBar';
import { InventoryItemsTable } from './inventory/InventoryItemsTable';
import { InventoryHistoryTable } from './inventory/InventoryHistoryTable';
import { InventoryAddModal } from './inventory/InventoryAddModal';
import { InventoryMergeModal } from './inventory/InventoryMergeModal';
import { csvRow, escapeCsvCell } from './reports/csvEscape';

interface InventoryTabProps {
  inventoryData:       InventoryData | null;
  lowStockCount:       number;
  lowThreshold:        number;
  locale:              string;
  departments:         Department[];
  staffDirectory:      StaffMember[];
  onInventoryChanged:  () => void;
  showToast:           (msg: string, type?: 'success' | 'error') => void;
}

export function InventoryTab({
  inventoryData, lowStockCount, lowThreshold, locale,
  departments, staffDirectory, onInventoryChanged, showToast,
}: InventoryTabProps) {
  const inventory = inventoryData?.inventory ?? [];
  const log       = inventoryData?.log ?? [];

  const deptMap = useMemo(() => {
    const map: Record<string, string> = {};
    departments.forEach(d => { if (d.id) map[d.id] = d.name; });
    return map;
  }, [departments]);

  const staffMap = useMemo(() => {
    const map: Record<string, string> = {};
    staffDirectory.forEach(s => { if (s.id) map[s.id] = s.name; });
    return map;
  }, [staffDirectory]);

  // ── Sub-tab + search state
  const [view, setView] = useState<'items' | 'history'>('items');
  const [search, setSearch] = useState('');

  const filteredInventory = useMemo(() => {
    if (!search.trim()) return inventory;
    const q = search.toLowerCase();
    return inventory.filter(i =>
      i.name?.toLowerCase().includes(q) ||
      i.code?.toLowerCase().includes(q) ||
      i.size?.toLowerCase().includes(q)
    );
  }, [inventory, search]);

  // ── History filters
  const [histSearch, setHistSearch] = useState('');
  const [histAction, setHistAction] = useState('all');
  const [histDateFrom, setHistDateFrom] = useState('');
  const [histDateTo, setHistDateTo] = useState('');

  const filteredLog = useMemo(() => log.filter(entry => {
    if (histAction !== 'all' && entry.action !== histAction) return false;
    if (histSearch.trim()) {
      const q = histSearch.toLowerCase();
      const matchesItem  = entry.item_name?.toLowerCase().includes(q);
      const matchesStaff = entry.performed_by?.toLowerCase().includes(q);
      const matchesNotes = entry.notes?.toLowerCase().includes(q);
      if (!matchesItem && !matchesStaff && !matchesNotes) return false;
    }
    if (histDateFrom || histDateTo) {
      const d = entry.timestamp?.slice(0, 10) || '';
      if (histDateFrom && d < histDateFrom) return false;
      if (histDateTo && d > histDateTo) return false;
    }
    return true;
  }), [log, histSearch, histAction, histDateFrom, histDateTo]);

  // ── History summary stats
  const now = useMemo(() => new Date(), []);
  const todayStr = now.toISOString().slice(0, 10);
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const todayTransactions = log.filter(e => (e.timestamp?.slice(0, 10) || '') === todayStr).length;
  const weekIssued   = log.filter(e => e.action === 'ISSUE'  && (e.timestamp?.slice(0, 10) || '') >= weekAgo).reduce((s, e) => s + e.quantity, 0);
  const weekReturned = log.filter(e => e.action === 'RETURN' && (e.timestamp?.slice(0, 10) || '') >= weekAgo).reduce((s, e) => s + e.quantity, 0);

  // ── Per-item history expansion
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  // ── Rename state
  const [renameItemId, setRenameItemId] = useState<string | null>(null);
  const [renameValue, setRenameValue]   = useState('');
  const [renameBusy, setRenameBusy]     = useState(false);

  const toggleItemHistory = (itemId: string) => {
    setExpandedItemId(prev => {
      if (prev !== itemId) setRenameItemId(null);
      return prev === itemId ? null : itemId;
    });
  };

  const handleRename = async (item: InventoryItem) => {
    if (!renameValue.trim()) return;
    setRenameBusy(true);
    const result = await inventoryRename(item.id, renameValue.trim());
    if (result.ok) {
      showToast(`Renamed to "${renameValue.trim()}"`, 'success');
      setRenameItemId(null);
      onInventoryChanged();
    } else {
      showToast(result.message || result.error || 'Failed to rename');
    }
    setRenameBusy(false);
  };

  // ── Merge modal
  const [mergeItemId, setMergeItemId]     = useState<string | null>(null);
  const [mergeSearch, setMergeSearch]     = useState('');
  const [mergeTargetId, setMergeTargetId] = useState<string | null>(null);
  const [mergeBusy, setMergeBusy]         = useState(false);
  const [mergeDropOpen, setMergeDropOpen] = useState(false);

  const mergeSource = mergeItemId ? inventory.find(i => i.id === mergeItemId) ?? null : null;
  const mergeOtherItems = mergeSource
    ? inventory.filter(i => i.id !== mergeSource.id && i.department_id === mergeSource.department_id)
    : [];

  const handleMerge = async () => {
    if (!mergeItemId || !mergeTargetId) return;
    const sourceItem = inventory.find(i => i.id === mergeItemId);
    if (!sourceItem) return;
    setMergeBusy(true);
    const result = await inventoryMerge(sourceItem.id, mergeTargetId);
    if (result.ok) {
      showToast(result.message || 'Items merged successfully', 'success');
      setMergeItemId(null); setMergeTargetId(null); setMergeSearch(''); setMergeDropOpen(false);
      setExpandedItemId(null);
      onInventoryChanged();
    } else {
      showToast(result.message || result.error || 'Failed to merge');
    }
    setMergeBusy(false);
  };

  const closeMergeModal = () => { setMergeItemId(null); setMergeTargetId(null); setMergeSearch(''); setMergeDropOpen(false); };

  // ── Add Item modal
  const [showAddItem, setShowAddItem] = useState(false);
  const [addBusy, setAddBusy]         = useState(false);
  const [formName, setFormName]       = useState('');
  const [formSku, setFormSku]         = useState('');
  const [formSize, setFormSize]       = useState('');
  const [formQty, setFormQty]         = useState(1);
  const [formDept, setFormDept]       = useState('');
  const [formThreshold, setFormThreshold] = useState(5);

  const resetForm = () => { setFormName(''); setFormSku(''); setFormSize(''); setFormQty(1); setFormDept(''); setFormThreshold(5); };
  const closeAddModal = () => { resetForm(); setShowAddItem(false); };

  const handleAddItem = async () => {
    if (!formName.trim()) { showToast('Please enter an item name'); return; }
    if (!formDept) { showToast('Please select a department'); return; }
    setAddBusy(true);
    const result = await inventoryAdd({
      item_name: formName.trim(), sku: formSku.trim() || undefined,
      size: formSize.trim() || undefined, quantity: formQty,
      low_stock_threshold: formThreshold, target_department_id: formDept,
    });
    if (result.ok) { showToast(`${formName.trim()} added to inventory`, 'success'); closeAddModal(); onInventoryChanged(); }
    else { showToast(result.message || result.error || 'Failed to add item'); }
    setAddBusy(false);
  };

  // ── Issue/Return inline
  const [actionId, setActionId]       = useState<string | null>(null);
  const [actionType, setActionType]   = useState<'issue' | 'return' | null>(null);
  const [actionQty, setActionQty]     = useState(1);
  const [actionStaff, setActionStaff] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [actionBusy, setActionBusy]   = useState(false);

  const openAction = (id: string, type: 'issue' | 'return') => { setActionId(id); setActionType(type); setActionQty(1); setActionStaff(''); setActionNotes(''); setExpandedItemId(null); };
  const closeAction = () => { setActionId(null); setActionType(null); setActionQty(1); setActionStaff(''); setActionNotes(''); };

  const handleAction = async () => {
    if (!actionId || !actionType || actionQty < 1) return;
    setActionBusy(true);
    const item = inventory.find(i => i.id === actionId);
    if (actionType === 'issue' && item && actionQty > item.available) {
      showToast(`Only ${item.available} available to issue`); setActionBusy(false); return;
    }
    const staffPrefix = actionStaff
      ? (actionType === 'issue' ? `Issued to: ${actionStaff}` : `Returned by: ${actionStaff}`)
      : '';
    const combinedNotes = [staffPrefix, actionNotes.trim()].filter(Boolean).join(' — ') || undefined;
    const result = actionType === 'issue'
      ? await inventoryIssue(actionId, actionQty, undefined, combinedNotes)
      : await inventoryReturn(actionId, actionQty, combinedNotes);
    if (result.ok) {
      const staffNote = actionStaff ? ` ${actionType === 'issue' ? 'to' : 'from'} ${actionStaff}` : '';
      showToast(`${actionQty} ${actionType === 'issue' ? 'issued' : 'returned'}${staffNote} successfully`, 'success');
      closeAction(); onInventoryChanged();
    } else { showToast(result.message || result.error || `Failed to ${actionType}`); }
    setActionBusy(false);
  };

  // ── Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { id, name } = deleteTarget;
    setDeleteTarget(null);
    const result = await inventoryDelete(id);
    if (result.ok) { showToast(`${name} deleted`, 'success'); onInventoryChanged(); }
    else { showToast(result.message || result.error || 'Failed to delete'); }
  };

  // ── CSV import/export
  const csvRef = useRef<HTMLInputElement>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [showMenu, setShowMenu]     = useState(false);
  const [rowMenuId, setRowMenuId]   = useState<string | null>(null);
  const [showActionDrop, setShowActionDrop] = useState(false);
  const [rowMenuPos, setRowMenuPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });

  const handleDownloadTemplate = () => {
    const headers = csvRow(['Item Name', 'SKU', 'Size', 'Quantity', 'Department', 'Low Stock Threshold']);
    const deptName = departments[0]?.name || 'Warehouse';
    const example1 = csvRow(['Safety Boots', 'SB-1234', 'Size 10', 20, deptName, 5]);
    const example2 = csvRow(['Nitrile Gloves', 'NG-5678', 'Large', 50, deptName, 10]);
    const example3 = csvRow(['Hard Hat', 'HH-9012', '', 30, departments[1]?.name || deptName, 5]);
    const refSection = ['', '# REFERENCE - delete these lines before uploading',
      escapeCsvCell(`# Valid Departments: ${departments.map(d => d.name).join(', ')}`),
      '# Quantity is the initial stock count',
      '# Low Stock Threshold: items at or below this number are flagged'];
    const csv = [headers, example1, example2, example3, ...refSection].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'gopal-inventory-template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return; setImportBusy(true);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l && !l.startsWith('#'));
      if (lines.length < 2) { showToast('CSV has no data rows'); setImportBusy(false); return; }
      const header = lines[0].toLowerCase();
      const si = header.includes('item name') || header.includes('name') ? 1 : 0;
      const dataLines = lines.slice(si);
      const deptNameMap: Record<string, string> = {};
      departments.forEach(d => { deptNameMap[d.name.toLowerCase()] = d.id; });
      let created = 0; let failed = 0; const errors: string[] = [];
      for (let idx = 0; idx < dataLines.length; idx++) {
        const cols = dataLines[idx].split(',').map(c => c.trim().replace(/^"|"$/g, ''));
        if (cols.length < 4) { errors.push(`Row ${idx + 1}: not enough columns`); continue; }
        const [name, sku, size, qtyStr, dept, threshStr] = cols;
        if (!name) { errors.push(`Row ${idx + 1}: missing item name`); continue; }
        if (!dept) { errors.push(`Row ${idx + 1}: missing department`); continue; }
        const deptId = deptNameMap[dept.toLowerCase()];
        if (!deptId) { errors.push(`Row ${idx + 1}: unknown department "${dept}"`); continue; }
        const result = await inventoryAdd({
          item_name: name, sku: sku || undefined, size: size || undefined,
          quantity: parseInt(qtyStr) || 1, low_stock_threshold: parseInt(threshStr) || 5,
          target_department_id: deptId,
        });
        if (result.ok) { created++; } else { failed++; }
      }
      const msg = `Imported ${created} item${created !== 1 ? 's' : ''}` +
        (failed > 0 ? `, ${failed} failed` : '') +
        (errors.length > 0 ? `, ${errors.length} row${errors.length !== 1 ? 's' : ''} skipped` : '');
      showToast(msg, failed === 0 && errors.length === 0 ? 'success' : 'error');
      if (created > 0) onInventoryChanged();
    } catch (err) { showToast(`Import error: ${err instanceof Error ? err.message : 'Unknown error'}`); }
    setImportBusy(false);
    if (csvRef.current) csvRef.current.value = '';
  };

  return (
    <div className="pane active">
      <input ref={csvRef} type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={handleImportCSV} />

      <InventoryStatCards
        view={view}
        inventory={inventory}
        lowStockCount={lowStockCount}
        todayTransactions={todayTransactions}
        weekIssued={weekIssued}
        weekReturned={weekReturned}
      />

      <InventoryFilterBar
        view={view}
        setView={setView}
        search={search}
        setSearch={setSearch}
        filteredCount={filteredInventory.length}
        histSearch={histSearch} setHistSearch={setHistSearch}
        histAction={histAction} setHistAction={setHistAction}
        histDateFrom={histDateFrom} setHistDateFrom={setHistDateFrom}
        histDateTo={histDateTo} setHistDateTo={setHistDateTo}
        showActionDrop={showActionDrop} setShowActionDrop={setShowActionDrop}
        showMenu={showMenu} setShowMenu={setShowMenu}
        importBusy={importBusy}
        onClearHistory={() => { setHistSearch(''); setHistAction('all'); setHistDateFrom(''); setHistDateTo(''); setShowActionDrop(false); }}
        onDownloadTemplate={handleDownloadTemplate}
        onImportClick={() => csvRef.current?.click()}
        onAddClick={() => setShowAddItem(true)}
        onViewItemsReset={() => { setExpandedItemId(null); setShowActionDrop(false); }}
        onViewHistoryReset={() => { closeAction(); setRowMenuId(null); }}
      />

      <InventoryAddModal
        open={showAddItem}
        onClose={closeAddModal}
        formName={formName} setFormName={setFormName}
        formSku={formSku} setFormSku={setFormSku}
        formSize={formSize} setFormSize={setFormSize}
        formQty={formQty} setFormQty={setFormQty}
        formDept={formDept} setFormDept={setFormDept}
        formThreshold={formThreshold} setFormThreshold={setFormThreshold}
        busy={addBusy}
        departments={departments}
        onSubmit={handleAddItem}
      />

      <InventoryMergeModal
        open={!!mergeItemId}
        onClose={closeMergeModal}
        sourceItem={mergeSource}
        otherItems={mergeOtherItems}
        search={mergeSearch} setSearch={setMergeSearch}
        targetId={mergeTargetId} setTargetId={setMergeTargetId}
        dropOpen={mergeDropOpen} setDropOpen={setMergeDropOpen}
        busy={mergeBusy}
        onConfirm={handleMerge}
      />

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete item"
        message={deleteTarget ? <>Delete <strong>{deleteTarget.name}</strong> from inventory? This cannot be undone.</> : null}
        confirmLabel="Delete"
        destructive
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      {view === 'items' && (
        <InventoryItemsTable
          filteredInventory={filteredInventory}
          lowThreshold={lowThreshold}
          locale={locale}
          search={search}
          deptMap={deptMap}
          staffMap={staffMap}
          staffDirectory={staffDirectory}
          log={log}
          expandedItemId={expandedItemId}
          onToggleHistory={toggleItemHistory}
          renameItemId={renameItemId}
          renameValue={renameValue} setRenameValue={setRenameValue}
          renameBusy={renameBusy}
          onStartRename={(item) => { setRenameItemId(item.id); setRenameValue(item.name); }}
          onCancelRename={() => setRenameItemId(null)}
          onSubmitRename={handleRename}
          onStartMerge={(id) => { setMergeItemId(id); setMergeSearch(''); setMergeTargetId(null); setMergeDropOpen(false); }}
          onCloseExpansion={() => setExpandedItemId(null)}
          rowMenuId={rowMenuId} setRowMenuId={setRowMenuId}
          rowMenuPos={rowMenuPos} setRowMenuPos={setRowMenuPos}
          onOpenIssue={(id) => openAction(id, 'issue')}
          onOpenReturn={(id) => openAction(id, 'return')}
          onDeleteItem={(id, name) => setDeleteTarget({ id, name })}
          actionId={actionId} actionType={actionType}
          actionQty={actionQty} setActionQty={setActionQty}
          actionStaff={actionStaff} setActionStaff={setActionStaff}
          actionNotes={actionNotes} setActionNotes={setActionNotes}
          actionBusy={actionBusy}
          onActionConfirm={handleAction}
          onActionCancel={closeAction}
          onAddClick={() => setShowAddItem(true)}
        />
      )}

      {view === 'history' && (
        <InventoryHistoryTable
          filteredLog={filteredLog}
          fullLog={log}
          locale={locale}
          staffMap={staffMap}
        />
      )}
    </div>
  );
}
